import { useAnalysisStore } from '../../stores/appAnalysisStore';
import { hasMainPageRestriction } from '../../services/accessManagement/mainPageRestriction';
import React, { useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Home, Clock8, Settings, LogOut, IdCardLanyard, Megaphone, DollarSign, Calculator, ChefHat, BarChart3, FileText, ClipboardCheck, UtensilsCrossed, Bell, ChevronRight, ChevronDown, BookOpen, Trash2, Package, TrendingUp, Shield, Target } from 'lucide-react'
import ThemeToggle from './ThemeToggle'
import StaffIcon from './StaffIcon'
import { CustomerRequestsBell } from '../customerRequests/CustomerRequestsBell';
import LanguageToggle from './LanguageToggle'
import MenuButton from './MenuButton'
import { useAuth } from '../../services/authentication/authContext'
import RestaurantSelector from './RestaurantSelector'
import { signOut as supabaseSignOut } from '../../services/authentication/supabaseService'
import { boldText, largeTextSize, primaryText } from '../../styling/stylingHelper'
import { cn } from '../../lib/utils'
import { isInvoiceOnlyAccount, canAccessInvoicesPage, canAccessRecipesPage, canAccessChecklistsPage, canAccessMepPage, canAccessNotificationsPage, canAccessWastePage, canAccessStaffPage, canAccessInventoryPage, canAccessReportsPage, canAccessAnalysisPage, canAccessSuperAdminDashboard } from '../../services/accessManagement/accessManagement';
import { Collapse } from './Collapse';
import { ImpersonationSelector } from './ImpersonationControls';
import { useAppUserDefaultStore, saveAppPagesExpandedSetting } from '../../stores/appUserDefaultStore';
import { useAppSettingsStore } from '../../stores/appSettingsStore';

const NavBar: React.FC = () => {
  const { t } = useTranslation()
  const hasUnseenAnalysis = useAnalysisStore(s => !s.analysisFindingsSeen && Object.values(s.wasteFindings).some(findings => findings.length > 0));
  const { user, backendUser, accountSettings, loading } = useAuth()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const appPagesExpanded = useAppUserDefaultStore(s => s.appPagesExpanded)
  const darkMode = useAppSettingsStore(s => s.darkMode)

  const userInitial = user?.email?.charAt(0).toUpperCase() || ''

  const sidebarMenuClass = `text-tertiary dark:text-tertiary-dark flex py-2 items-center gap-2 p-2 rounded-xl font-medium hover:text-textGreen dark:hover:text-text-dark hover:bg-cardLightGreen/80 dark:hover:bg-cardLightGreen-dark/80`
  const sidebarMenuActiveClass = 'bg-cardLightGreen dark:bg-cardLightGreen-dark text-textGreen dark:text-text-dark'

  const handleLogout = async () => {
    try {
      await supabaseSignOut()
      navigate('/login', { replace: true })
    } catch (err) {
      console.error('Logout error', err)
    }
  }

  const handleToggleAppPages = async () => {
    try {
      await saveAppPagesExpandedSetting(!appPagesExpanded)
    } catch (error) {
      console.error('Failed to save app pages expanded setting')
    }
  }

  if (!user) return null;

  // Check user access for special features
  const hasRecipesAccess = canAccessRecipesPage(backendUser, accountSettings);
  const hasChecklistsAccess = canAccessChecklistsPage(backendUser, accountSettings);
  const hasMepAccess = canAccessMepPage(backendUser, accountSettings);
  const hasInvoicesAccess = canAccessInvoicesPage(backendUser, accountSettings);
  const hasNotificationsAccess = canAccessNotificationsPage(backendUser, accountSettings);
  const hasWasteAccess = canAccessWastePage(backendUser, accountSettings);
  const hasStaffAccess = canAccessStaffPage(backendUser);
  const hasInventoryAccess = canAccessInventoryPage(backendUser, accountSettings);
  const hasReportsAccess = canAccessReportsPage(backendUser, accountSettings);
  // Analysis is hidden for restricted staff and in invoice-only mode (same rule as BottomBar).
  const hasAnalysisAccess = !!backendUser && !hasMainPageRestriction(backendUser) && !isInvoiceOnlyAccount(backendUser, accountSettings) && canAccessAnalysisPage(backendUser, accountSettings);

  return (
    <nav className="bg-zinc-50 dark:bg-backgroundHeader-dark shadow-sm border-b border-zinc-200 dark:border-borderGrey-dark">
      <div className="px-3 py-2 min-[650px]:px-6 min-[650px]:py-3">
        <div className="flex items-center justify-between">
          {/* Logo & Title */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src={darkMode ? "/app_logo.png" : "/logo.png"}
              alt="Culinary Cloud Logo"
              className={`w-10 h-10 min-[650px]:w-11 min-[650px]:h-11 ${darkMode ? "object-cover rounded-full" : "object-contain"}`}
            />
            <span className={cn(largeTextSize, boldText, primaryText)}>
              {t('nav.title')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4">
            {/* {user && (
              <>
                <SearchIcon />
                <ImportIcon />
                <ReportsIcon />
                <OrderHistoryIcon />
              </>
            )}  */}
            <CustomerRequestsBell />
            {hasStaffAccess && (hasMainPageRestriction(backendUser) || !isInvoiceOnlyAccount(backendUser, accountSettings)) && <StaffIcon />}
            <LanguageToggle />
            <ThemeToggle />

            {user && (
              <>
                {backendUser && canAccessSuperAdminDashboard(backendUser) && (
                  <Link
                    to="/admin"
                    title={t('nav.admin')}
                    className="flex items-center gap-1.5 h-9 px-3 rounded-xl border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm font-medium"
                  >
                    <Shield className="w-5 h-5" />
                    {t('nav.admin')}
                  </Link>
                )}
                <ImpersonationSelector />
                <RestaurantSelector />
              </>
            )}

            {!loading && !user && (
              <>
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Go to dashboard / login */}
                  <a
                    href="/dashboard"
                    className="hidden sm:inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
                  >
                    Go to dashboard
                  </a>
                </div>
                {/* <Link
                  to="/login"
                  className="px-4 py-2 bg-secondary dark:bg-secondary-dark text-white dark:text-white rounded hover:bg-secondary-light dark:hover:bg-secondary transition"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 bg-success dark:bg-success-dark text-white dark:text-white rounded hover:bg-success-light dark:hover:bg-success transition"
                >
                  {t('nav.signup')}
                </Link> */}
              </>
            )}


            {!loading && user && (
              <Link to="/profile">
                {backendUser?.profile_picture_url ? (
                  <img
                    src={backendUser.profile_picture_url}
                    alt="Profile"
                    className="h-11 w-11 rounded-full object-cover hover:scale-95 transition-transform duration-200 border-2 border-gray-300 dark:border-gray-600"
                  />
                ) : (
                  <div className="h-11 w-11 rounded-full bg-gray-400 dark:bg-gray-500 flex items-center justify-center text-white dark:text-white uppercase text-sm hover:scale-95 transition-transform duration-200">
                    {userInitial}
                  </div>
                )}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button and Profile — always hidden now: the BottomBar owns
              mobile navigation (and MobilePageHeader the top area). */}
          <div className="hidden items-center gap-3">
            <MenuButton isOpen={isMenuOpen} onClick={() => setIsMenuOpen(!isMenuOpen)} />
            {!loading && user && (
              <Link to="/profile">
                {backendUser?.profile_picture_url ? (
                  <img
                    src={backendUser.profile_picture_url}
                    alt="Profile"
                    className="h-10 w-10 rounded-full object-cover hover:scale-95 transition-transform duration-200 border-2 border-gray-300 dark:border-gray-600"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-400 dark:bg-gray-500 flex items-center justify-center text-white dark:text-white uppercase text-sm hover:scale-95 transition-transform duration-200">
                    {userInitial}
                  </div>
                )}
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`${
            isMenuOpen ? 'max-h-[calc(100vh-100px)] opacity-100' : 'max-h-0 opacity-0'
          } hidden overflow-hidden transition-all duration-200 ease-in-out`}
        >
          <div className="py-3 space-y-3">
            {/* First row: Language/Theme on left, RestaurantSelector on right */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LanguageToggle />
                <ThemeToggle />
              </div>
              {user && (
                <div className="flex items-center gap-3">
                  {backendUser && canAccessSuperAdminDashboard(backendUser) && (
                    <Link
                      to="/admin"
                      title={t('nav.admin')}
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-1.5 h-9 px-3 rounded-xl border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm font-medium"
                    >
                      <Shield className="w-5 h-5" />
                      {t('nav.admin')}
                    </Link>
                  )}
                  <ImpersonationSelector />
                  <RestaurantSelector />
                </div>
              )}
            </div>

            {/* Page Navigation Links */}
            {user && backendUser && (
              <>
                <ul className="flex flex-col space-y-2">
                  <li>
                    <NavLink
                      to="/dashboard"
                      className={({ isActive }) =>
                        `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Home className="w-6 h-6" />
                      {t('nav.home')}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/revenuehistory"
                      className={({ isActive }) =>
                        `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <DollarSign className="w-6 h-6" />
                      {t('nav.revenueHistory')}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/orderhistory"
                      className={({ isActive }) =>
                        `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Clock8 className="w-6 h-6" />
                      {t('nav.orderHistory')}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/pricealerts"
                      className={({ isActive }) =>
                        `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Megaphone className="w-6 h-6" />
                      {t('nav.priceAlerts')}
                    </NavLink>
                  </li>
                  {/* {isAllRestaurants && (
                    <li>
                      <NavLink
                        to="/crossrestaurantprices"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <GitCompare className="w-6 h-6" />
                        {t('nav.crossRestaurantPrices')}
                      </NavLink>
                    </li>
                  )} */}
                  <li>
                    <NavLink
                      to="/costprice"
                      className={({ isActive }) =>
                        `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Calculator className="w-6 h-6" />
                      {t('nav.costPrice')}
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/menus"
                      className={({ isActive }) =>
                        `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <BookOpen className="w-6 h-6" />
                      {t('nav.menus')}
                    </NavLink>
                  </li>
                  {hasInvoicesAccess && (
                    <li>
                      <NavLink
                        to="/invoices"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <FileText className="w-6 h-6" />
                        {t('nav.invoices')}
                      </NavLink>
                    </li>
                  )}
                  <li>
                    <NavLink
                      to="/charts"
                      className={({ isActive }) =>
                        `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                      }
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <BarChart3 className="w-6 h-6" />
                      {t('nav.charts')}
                    </NavLink>
                  </li>
                  {hasReportsAccess && (
                    <li>
                      <NavLink
                        to="/reports"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <TrendingUp className="w-6 h-6" />
                        {t('nav.reports')}
                      </NavLink>
                    </li>
                  )}
                  {hasAnalysisAccess && (
                    <li>
                      <NavLink
                        to="/analysis"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Target className="w-6 h-6" />
                        {t('analysis.navLabel')}
                        {hasUnseenAnalysis && <span className="ml-auto rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-xs font-semibold min-w-5 h-5 px-1 flex items-center justify-center">1</span>}
                      </NavLink>
                    </li>
                  )}
                </ul>

                {/* App Pages collapsible section */}
                {(hasRecipesAccess || hasChecklistsAccess || hasMepAccess || hasNotificationsAccess || hasWasteAccess || hasInventoryAccess) && (
                  <div className="mt-4">
                    <button
                      onClick={handleToggleAppPages}
                      className="w-full flex items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wider text-muted dark:text-muted-dark hover:text-tertiary dark:hover:text-tertiary-dark"
                    >
                      {t('nav.appPages')}
                      {appPagesExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    <Collapse open={appPagesExpanded}>
                      <ul className="flex flex-col space-y-2 pl-4 mt-2">
                        {hasRecipesAccess && (
                          <li>
                            <NavLink
                              to="/recipes"
                              className={({ isActive }) =>
                                `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                              }
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <ChefHat className="w-6 h-6" />
                              {t('nav.recipes')}
                            </NavLink>
                          </li>
                        )}
                        {hasChecklistsAccess && (
                          <li>
                            <NavLink
                              to="/checklists"
                              className={({ isActive }) =>
                                `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                              }
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <ClipboardCheck className="w-6 h-6" />
                              {t('nav.checklists')}
                            </NavLink>
                          </li>
                        )}
                        {hasMepAccess && (
                          <li>
                            <NavLink
                              to="/mep"
                              className={({ isActive }) =>
                                `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                              }
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <UtensilsCrossed className="w-6 h-6" />
                              {t('nav.mep')}
                            </NavLink>
                          </li>
                        )}
                        {hasInventoryAccess && (
                          <li>
                            <NavLink
                              to="/inventory"
                              className={({ isActive }) =>
                                `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                              }
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <Package className="w-6 h-6" />
                              {t('nav.inventory')}
                            </NavLink>
                          </li>
                        )}
                        {hasNotificationsAccess && (
                          <li>
                            <NavLink
                              to="/notifications"
                              className={({ isActive }) =>
                                `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                              }
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <Bell className="w-6 h-6" />
                              {t('nav.notifications')}
                            </NavLink>
                          </li>
                        )}
                        {hasWasteAccess && (
                          <li>
                            <NavLink
                              to="/waste"
                              className={({ isActive }) =>
                                `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                              }
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <Trash2 className="w-6 h-6" />
                              {t('nav.wasteTracking')}
                            </NavLink>
                          </li>
                        )}
                      </ul>
                    </Collapse>
                  </div>
                )}

                {/* Bottom menu items */}
                <ul className="flex flex-col space-y-2 mt-auto pt-4">
                  {hasStaffAccess && (
                    <li>
                      <NavLink
                        to="/staff"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <IdCardLanyard className="w-6 h-6" />
                        {t('nav.staff')}
                      </NavLink>
                    </li>
                  )}
                  {backendUser.role === 'admin' && (
                    <li>
                      <NavLink
                        to="/settings"
                        className={({ isActive }) =>
                          `${sidebarMenuClass} ${isActive ? sidebarMenuActiveClass : ''}`
                        }
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Settings className="w-6 h-6" />
                        {t('nav.settings')}
                      </NavLink>
                    </li>
                  )}
                  <li>
                    <button 
                      onClick={() => {
                        handleLogout()
                        setIsMenuOpen(false)
                      }}
                      className={`${sidebarMenuClass} w-full text-left`}
                    >
                      <LogOut className="w-6 h-6" />
                      {t('nav.logout')}
                    </button>
                  </li>
                </ul>
              </>
            )}

            {!loading && !user && (
              <div className="flex items-center gap-2 sm:gap-3">
                <a
                  href="/dashboard"
                  className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
                >
                  Go to dashboard
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

export default NavBar